export default function LafayetteTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Lafayette Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Lafayette travelers actually move helps us anticipate the passport urgencies that arise here. From oilfield rotation timelines to university study-abroad deadlines, Acadiana travel operates on a rhythm driven by energy industry cycles, academic calendars, and Cajun cultural connections.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-oil-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Oil &amp; Gas Rotation Assignments</h3>
                <p className="text-gray-400 text-xs">West Africa, North Sea, Middle East, Latin America</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Lafayette is headquarters to dozens of oilfield service companies. Rotation assignments to Angola, Nigeria, the North Sea, Qatar, and the Gulf of Mexico can be confirmed with as little as ten days notice. Engineers, geologists, and project managers in the energy corridor frequently discover their passports have less than six months validity — which can disqualify entry to some countries — only after receiving travel orders.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Many oil-producing countries require six months passport validity beyond your departure date. Check your expiration the moment a rotation assignment is confirmed — do not wait until your flight paperwork is submitted.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">ULL Study Abroad &amp; Exchange Programs</h3>
                <p className="text-gray-400 text-xs">France, Spain, Canada, Latin America</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              University of Louisiana at Lafayette has strong ties to francophone academic institutions — in France, Quebec, and French-speaking African nations. Many ULL students accept exchange placements with two to three months lead time, only to discover their passport has expired or they have never obtained one. Spring semester exchanges and summer study programs create predictable windows of urgent passport demand each October through January.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> First-time applicants (many ULL students) require an in-person acceptance clerk visit. Apply as soon as your program acceptance letter arrives — orientation departure dates do not flex.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-music-2-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Cajun Heritage &amp; Cultural Travel</h3>
                <p className="text-gray-400 text-xs">France, Quebec, Martinique, French Caribbean</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Lafayette's Cajun and Creole heritage creates unique travel patterns. Many Acadiana families maintain connections to communities in France's Poitou-Charentes region, Quebec's Acadian Coast, and French Caribbean islands. Heritage tours, family reunions in France, and cultural exchange trips with French musicians and chefs generate a steady stream of passport renewals for travelers who may not have used their passport in a decade or more.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> If your last international trip was before 2020, your passport likely expired during the pandemic travel lull. Check your expiration before booking flights or festival registrations.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-ship-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">New Orleans Cruises &amp; Gulf Travel</h3>
                <p className="text-gray-400 text-xs">Bahamas, Caribbean, Mexico from NOA &amp; Galveston</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Lafayette families regularly book cruises from the Port of New Orleans (130 miles east) and Port of Galveston (200 miles west). Last-minute cruise deals and holiday bookings often come together in three to six weeks, leaving little time to discover a child's passport is expired or a spouse never had one. Acadiana families are avid cruise travelers, and passport oversight is one of the most common pre-cruise crises we resolve.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Even Bahamas cruises may require passports for all passengers when flying to rejoin the ship. Verify every family member's passport status before booking — not the week of departure.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-flight-takeoff-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Lafayette Regional Airport (LFT) &amp; International Connections</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Lafayette Regional Airport (LFT) connects Acadiana travelers to Dallas-Fort Worth, Houston, Atlanta, and Charlotte — all major international hubs. Many Lafayette residents also drive to New Orleans Louis Armstrong International (MSY, 130 miles) for direct transatlantic flights. Whether flying from LFT or MSY, your passport must be in hand before your connection date.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}